if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        // ---------- MAX based on the input[max] attribute ----------
        const qtyInput = this.form.querySelector('input[name="quantity"]');
        const maxAttr = qtyInput?.getAttribute('max');
        const maxQty = (maxAttr != null && maxAttr !== '')
          ? Number(maxAttr)
          : Number.POSITIVE_INFINITY;

        const variantId = Number(formData.get('id'));
        let requestedQty = Number(formData.get('quantity') || qtyInput?.value || 1);


        const existingLine = Array.isArray(oldCartData?.items)
          ? oldCartData.items.find(item => Number(item.variant_id) === Number(variantId))
          : null;

        const existingQty = existingLine?.quantity ? Number(existingLine.quantity) : 0;


        if (Number.isFinite(maxQty) && existingQty + requestedQty > maxQty) {
          this.setErrorMsgState(`Maximum allowed for this item is ${maxQty}.`);
          throw new Error('MAX_QTY_VIOLATION');
        }
        // -----------------------------------------------------------

        // ===================== INSERTED: JSON add via /cart/add.js =====================
        // Build JSON payload from your existing form data.
        const payload = { id: variantId, quantity: requestedQty };

        // Include line item properties if present (properties[Key]=Value)
        const props = {};
        for (const [k, v] of formData.entries()) {
          if (k.startsWith('properties[') && k.endsWith(']')) {
            const key = k.slice(11, -1); // strip "properties[" and trailing "]"
            props[key] = v;
          }
        }
        if (Object.keys(props).length) payload.properties = props;

        // Perform JSON add (no redirect)
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await response.json();

        // Fetch sections HTML so your existing UI updates still work
        try {
          const secResp = await fetch(
            `${window.location.pathname}?sections=${encodeURIComponent(sections)}&sections_url=${encodeURIComponent(window.location.pathname)}`,
            { headers: { 'X-Requested-With': 'XMLHttpRequest' } }
          );
          if (secResp.ok) {
            const secJson = await secResp.json();
            // Attach to data so downstream logic can use data.sections
            data.sections = secJson;
          }
        } catch (e) {
          // Non-fatal if sections fetch fails; UI fallbacks will still run.
        }
        // ==============================================================================

        // >>> Reflect server "Only N items were added..." in the UI
        const serverMsg = (typeof data.description === 'string' ? data.description : data.message) || '';
        const match = serverMsg.match(/Only\s+(\d+)\s+items?\s+were\s+added/i);
        if (match) {
          const allowed = Number(match[1]); // use what Shopify actually accepted
          if (Number.isFinite(allowed) && qtyInput) {
            qtyInput.value = String(allowed);             // reflect what actually got added
            qtyInput.setAttribute('max', String(allowed)); // prevent requesting more (for now)
          }
          this.setErrorMsgState(serverMsg);               // show Shopify’s message in your UI
        }
        // <<< end reflect

        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
